import {z} from 'zod'
import {REST} from '../../../api'
import {
    userPreferenceSchema,
    userPreferencesArraySchema,
    type UserPreference,
    type RawUserPreference,
    type UpdateUserPreferenceRequest,
    type TableFiltersPreferences,
    PREFERENCE_KEYS,
} from './types/user-preferences-management.types'
import type {AxiosRequestConfig} from 'axios'

const USER_PREFS_URL = '/api/v1/userPreferences'

export async function getUserPreference<T = unknown>(
    key: string,
    config?: AxiosRequestConfig,
): Promise<UserPreference<T> | null> {
    try {
        const response = await REST.get<unknown>(`${USER_PREFS_URL}/${key}`, config)
        const validated = userPreferenceSchema.parse(response.data)
        return validated as UserPreference<T>
    } catch (error) {
        if (error instanceof z.ZodError) {
            throw new Error(`Invalid user preference data format: ${error.message}`)
        }
        return null
    }
}

export async function getRawUserPreference<T = unknown>(
    key: string,
    config?: AxiosRequestConfig,
): Promise<RawUserPreference<T> | null> {
    try {
        const response = await REST.get<unknown>(`${USER_PREFS_URL}/${key}`, config)
        const validated = userPreferenceSchema.parse(response.data)
        return validated as RawUserPreference<T>
    } catch (error) {
        if (error instanceof z.ZodError) {
            throw new Error(`Invalid user preference data format: ${error.message}`)
        }
        return null
    }
}

export async function getAllUserPreferences<T = unknown>(
    config?: AxiosRequestConfig,
): Promise<UserPreference<T>[]> {
    try {
        const response = await REST.get<unknown>(USER_PREFS_URL, config)
        const validated = userPreferencesArraySchema.parse(response.data)
        return validated as UserPreference<T>[]
    } catch (error) {
        if (error instanceof z.ZodError) {
            throw new Error(`Invalid user preferences data format: ${error.message}`)
        }
        throw new Error(
            `Failed to fetch user preferences: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function updateUserPreference<T = unknown>(
    key: string,
    request: UpdateUserPreferenceRequest<T>,
    config?: AxiosRequestConfig,
): Promise<UserPreference<T>> {
    try {
        const response = await REST.put<unknown>(`${USER_PREFS_URL}/${key}`, request, config)
        const validated = userPreferenceSchema.parse(response.data)
        return validated as UserPreference<T>
    } catch (error) {
        if (error instanceof z.ZodError) {
            throw new Error(`Invalid user preference response format: ${error.message}`)
        }
        throw new Error(
            `Failed to update user preference: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function updateRawUserPreference<T = unknown>(
    key: string,
    value: T,
    config?: AxiosRequestConfig,
): Promise<RawUserPreference<T>> {
    try {
        const response = await REST.put<unknown>(`${USER_PREFS_URL}/${key}`, value, config)
        const validated = userPreferenceSchema.parse(response.data)
        return validated as RawUserPreference<T>
    } catch (error) {
        if (error instanceof z.ZodError) {
            throw new Error(`Invalid user preference response format: ${error.message}`)
        }
        throw new Error(
            `Failed to update user preference: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function deleteUserPreference(
    key: string,
    config?: AxiosRequestConfig,
): Promise<void> {
    try {
        await REST.delete(`${USER_PREFS_URL}/${key}`, config)
    } catch (error) {
        throw new Error(
            `Failed to delete user preference: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function getTableFiltersPreference(): Promise<TableFiltersPreferences> {
    const pref = await getRawUserPreference<TableFiltersPreferences>(PREFERENCE_KEYS.TABLE_FILTERS)
    return pref?.value ?? {}
}

export async function updateTableFiltersPreference(
    filters: TableFiltersPreferences,
): Promise<void> {
    await updateRawUserPreference(PREFERENCE_KEYS.TABLE_FILTERS, filters)
}

export type {
    UserPreference,
    RawUserPreference,
    CreateUserPreferenceRequest,
    UpdateUserPreferenceRequest,
    LiveAssetDetectionFilterPreferences,
    TableFiltersPreferences,
} from './types/user-preferences-management.types'

export {PREFERENCE_KEYS} from './types/user-preferences-management.types'
